import { createContext, useEffect, useRef, useState, type Ref } from "react";
import { Form, useActionData, useNavigation, type FormProps } from "react-router";
import { isNetworkFailureResult } from "~/lib/network-failure-result";

/** The form whose submission produced the route's current action data (one per page). */
let lastSubmitter: symbol | null = null;

export const ActionFormContext = createContext<{
  pending: boolean;
  /** A write is in flight: sibling submitters wait too. A GET search never blocks them. */
  mutation: boolean;
  submitter: HTMLElement | null;
} | null>(null);

/** A navigation form owns its wait, including loader revalidation after the write.
 * Fetcher forms retain their own fetcher's state instead of borrowing navigation. */
export function ActionForm({ onSubmit, children, ...props }: FormProps & { ref?: Ref<HTMLFormElement> }) {
  const navigation = useNavigation();
  const actionData = useActionData();
  const self = useRef(Symbol("action-form"));
  const inFlight = useRef(false);
  const startedWith = useRef(navigation);
  const restoreFocus = useRef<HTMLElement | null>(null);
  const [submission, setSubmission] = useState({ pending: false, mutation: false, submitter: null as HTMLElement | null });

  useEffect(() => {
    if (navigation !== startedWith.current && navigation.state === "idle") {
      inFlight.current = false;
      // The wait never disables natively (Button uses aria-disabled), but a re-render can still
      // unmount the active node: restore only if focus fell to the body, never if she moved on.
      if (submission.pending && document.activeElement === document.body) restoreFocus.current = submission.submitter;
      setSubmission(previous => previous.pending ? { pending: false, mutation: false, submitter: null } : previous);
    }
  }, [navigation, submission.pending, submission.submitter]);

  useEffect(() => {
    if (submission.pending) return;
    const target = restoreFocus.current;
    restoreFocus.current = null;
    if (target?.isConnected && document.activeElement === document.body) target.focus();
  }, [submission.pending]);

  return (
    <ActionFormContext value={submission}>
      <Form
        {...props}
        onSubmit={event => {
          const nativeSubmitter = (event.nativeEvent as SubmitEvent).submitter;
          const method = (nativeSubmitter?.getAttribute("formmethod") ?? props.method ?? "get").toLowerCase();
          const mutation = method !== "get";
          // A new GET filter/search must replace its older request. Only writes
          // suppress repeated submissions while their action is still pending.
          if (mutation && inFlight.current) {
            event.preventDefault();
            return;
          }
          onSubmit?.(event);
          if (event.defaultPrevented) return;
          inFlight.current = mutation;
          startedWith.current = navigation;
          const submitter = nativeSubmitter
            ?? event.currentTarget.querySelector<HTMLButtonElement>('button[type="submit"]:not([disabled]):not([aria-hidden="true"])');
          setSubmission({ pending: true, mutation, submitter });
          if (mutation) lastSubmitter = self.current;
        }}
      >
        {children}
        {/* T972 (preview QA) — the route's `clientAction` turned a failed fetch into the
            network marker (Back Office, English): the form and its values stay, and only
            the form that sent it says so. */}
        {lastSubmitter === self.current && isNetworkFailureResult(actionData) && !submission.pending ? (
          <p role="alert" className="mt-3 rounded-lg border border-red-700 bg-red-900/40 px-4 py-3 text-sm text-red-300">
            Could not reach the server — the change was not confirmed. Your form is kept: check and try again.
          </p>
        ) : null}
      </Form>
    </ActionFormContext>
  );
}
